import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { ImpactHeroDocument } from "@/components/impactReport/ImpactHeroDocument";
import { ImpactReportColophon } from "@/components/impactReport/ImpactReportColophon";
import { ImpactReportDeferredState } from "@/components/impactReport/ImpactReportDeferredState";
import { ImpactReportMasthead } from "@/components/impactReport/ImpactReportMasthead";
import { resolveImpactReportErrorMessage } from "@/components/impactReport/impact-report-error";
import {
  IMPACT_REPORT_ERROR_STATE_FALLBACK_BODY,
  IMPACT_REPORT_ERROR_STATE_TITLE,
  IMPACT_REPORT_JOB_BACK_LINK_LABEL,
  IMPACT_REPORT_JOB_EMPTY_BODY,
  IMPACT_REPORT_JOB_FAILED_BODY,
  IMPACT_REPORT_JOB_FAILED_TITLE,
  IMPACT_REPORT_JOB_NOT_FOUND_BODY,
  IMPACT_REPORT_JOB_NOT_FOUND_TITLE,
  IMPACT_REPORT_ROUTE_PATH,
  IMPACT_REPORT_UNAVAILABLE_STATE_BODY,
  IMPACT_REPORT_UNAVAILABLE_STATE_TITLE,
} from "@/constants/impactReport";
import { useImpactReportJob } from "@/hooks/useImpactReport";
import {
  buildImpactReportPageModel,
  describeImpactReportError,
  isImpactReportJobNotFoundError,
  isImpactReportUnavailableError,
  resolveImpactReportPhotoCredits,
} from "@/utils/impact-report-helper";

/**
 * `/impact/reports/:jobId` — the emailed link to a deferred report. Only the user who requested
 * the report can read it; the API answers 404 for an expired, unknown or someone else's job.
 *
 * Standalone by design: no control bar and no `ImpactReportContext`. The finished report renders
 * as the hero document with no cause selection, since this page has no way to know or change one.
 */
export default function ImpactReportJob() {
  const { jobId } = useParams<{ jobId: string }>();
  const { data: job, error, isError } = useImpactReportJob(jobId ?? null);

  const report = job?.status === "completed" ? job.report : undefined;
  const pages = useMemo(() => (report ? buildImpactReportPageModel(report, false) : []), [report]);

  useEffect(() => {
    if (!error) return;
    console.error("ImpactReportJob: report job request failed", describeImpactReportError(error));
  }, [error]);

  if (!jobId || (isError && isImpactReportJobNotFoundError(error))) {
    return (
      <ImpactReportJobMessage
        title={IMPACT_REPORT_JOB_NOT_FOUND_TITLE}
        body={IMPACT_REPORT_JOB_NOT_FOUND_BODY}
      />
    );
  }

  if (isError) {
    const isUnavailable = isImpactReportUnavailableError(error);
    return (
      <ImpactReportJobMessage
        title={
          isUnavailable ? IMPACT_REPORT_UNAVAILABLE_STATE_TITLE : IMPACT_REPORT_ERROR_STATE_TITLE
        }
        body={
          isUnavailable
            ? IMPACT_REPORT_UNAVAILABLE_STATE_BODY
            : resolveImpactReportErrorMessage(error, IMPACT_REPORT_ERROR_STATE_FALLBACK_BODY)
        }
      />
    );
  }

  if (job?.status === "failed") {
    return (
      <ImpactReportJobMessage
        title={IMPACT_REPORT_JOB_FAILED_TITLE}
        body={IMPACT_REPORT_JOB_FAILED_BODY}
      />
    );
  }

  if (!report) {
    return <ImpactReportDeferredState />;
  }

  return (
    <Box className="irv-report">
      <ImpactReportMasthead dateRange={report.data.dateRange} />
      {report.data.causeData.length === 0 ? (
        <Typography
          sx={{ my: "48px", fontSize: "14px", color: "var(--color-graphite)", textAlign: "center" }}
        >
          {IMPACT_REPORT_JOB_EMPTY_BODY}
        </Typography>
      ) : (
        <>
          <ImpactHeroDocument pages={pages} />
          <ImpactReportColophon
            photoCredits={resolveImpactReportPhotoCredits(
              report.data.thankYou?.photoCredits,
              false,
            )}
          />
        </>
      )}
    </Box>
  );
}

interface ImpactReportJobMessageProps {
  title: string;
  body: string;
}

/** Every state with no report to show: the words are the alert, the link back is its sibling. */
function ImpactReportJobMessage({ title, body }: ImpactReportJobMessageProps) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "320px",
        gap: 1.5,
      }}
    >
      <Box
        role="alert"
        sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "var(--color-rodeo-black)" }}>
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: "14px",
            color: "var(--color-graphite)",
            textAlign: "center",
            maxWidth: "360px",
          }}
        >
          {body}
        </Typography>
      </Box>
      <Button
        component={Link}
        to={IMPACT_REPORT_ROUTE_PATH}
        variant="outlined"
        sx={{ borderColor: "var(--color-dark-dust)", color: "var(--color-rodeo-black)" }}
      >
        {IMPACT_REPORT_JOB_BACK_LINK_LABEL}
      </Button>
    </Box>
  );
}
